import { SLOT_DECADES, type Decade, type Track } from "../../shared/domain.ts";

/**
 * The Daily Set: five Tracks every player receives identically on a given day.
 *
 * Selection is a pure function of the date, so there is no server-side draw to
 * keep in sync and no database of past puzzles — the same date always yields
 * the same five Tracks, for everyone, forever.
 */

/** xmur3 — turns a date string into a well-mixed 32-bit seed. */
function seedFrom(text: string): number {
  let h = 1779033703 ^ text.length;
  for (let i = 0; i < text.length; i++) {
    h = Math.imul(h ^ text.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return (h ^= h >>> 16) >>> 0;
}

/** mulberry32 — small, fast, and identical across every engine we care about. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The player's own calendar date — Rollover is local midnight, not UTC. */
export function localDateKey(now: Date): string {
  const y = now.getFullYear();
  const m = `${now.getMonth() + 1}`.padStart(2, "0");
  const d = `${now.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * How well known a Track must be to appear in each Slot, as a decade percentile.
 *
 * Two earlier attempts, both measured, both wrong:
 *
 *  1. A percentile-*weighted* draw (exponents 8/5/3/2/1) landed the five Slots
 *     at ranks 11, 167, 137, 279 and 428 of ~700. Only the opening Slot was
 *     reliably famous. `percentile ** b` puts the mean draw at (b+1)/(b+2) of the
 *     Pool, so reaching the top 3% would need an exponent near 32 — a floor with
 *     extra steps.
 *  2. A *fractional* floor — top 2% of an era for Slot 1, top 25% for Slot 5.
 *     This works at one Catalog size and quietly rots at any other: when the
 *     Catalog grew from 2,947 to 7,993 Tracks the same 25% became 2,000 songs,
 *     and Slot 5 started serving ranks 356–463 instead of ~170. Growing the
 *     Catalog made the game *harder*, which is precisely backwards.
 *
 * A percentile threshold has neither problem. `percentile` is a Track's standing
 * within its own decade, so the bar means the same thing at any Catalog size, it
 * behaves identically for an era Slot and the wildcard, and a bigger harvest
 * yields *more* candidates at the same difficulty rather than deeper ones.
 */
const SLOT_MIN_PERCENTILE = [0.985, 0.97, 0.95, 0.93, 0.9];

/**
 * Never narrow a Slot below this many candidates.
 *
 * The escape hatch for a Pool too thin to honour the bar — a Slot with fifteen
 * candidates would repeat within a fortnight. A thin era degrades to "the best it
 * has" rather than to a handful on repeat.
 */
const MIN_SLOT_CANDIDATES = 60;

/** The famous head of a Pool: everything clearing the Slot's bar. */
function headOf(pool: Track[], minPercentile: number): Track[] {
  const eligible = pool.filter((t) => t.percentile >= minPercentile);
  if (eligible.length >= MIN_SLOT_CANDIDATES) return eligible;
  // Too thin for the bar: take the best it has, best-first.
  return [...pool]
    .sort((a, b) => b.percentile - a.percentile)
    .slice(0, Math.min(pool.length, MIN_SLOT_CANDIDATES));
}

function pickFrom(pool: Track[], random: () => number, used: Set<number>): Track | null {
  // Never falls back to an already-used Track: a Daily Set containing the same
  // song twice would waste a Slot and look broken.
  const available = pool.filter((t) => !used.has(t.id));
  if (available.length === 0) return null;

  // Uniform within the head. The head has already done the filtering, and
  // weighting on top of it only re-concentrates the same few songs.
  return available[Math.floor(random() * available.length)] ?? null;
}

/**
 * Builds the day's five Slots — 2020s, 2010s, 2000s, pre-2000s, then a wildcard.
 *
 * The era split is what stops a Set clustering in one corner of the Catalog,
 * and it is why Rock and Hip Hop appear naturally across a Set rather than
 * needing lanes of their own.
 *
 * Each Slot draws uniformly from the famous head of its era, and the head gets
 * deeper as the Set goes on — so a Set opens on something almost everyone knows
 * and ends on something merely well-known. See SLOT_DEPTH.
 */
export function selectDailySet(tracks: Track[], dateKey: string): Track[] {
  const random = seededRandom(seedFrom(dateKey));
  const used = new Set<number>();
  const set: Track[] = [];

  SLOT_DECADES.forEach((slot, index) => {
    const era =
      slot === "wildcard" ? tracks : tracks.filter((t) => t.decade === (slot as Decade));
    const bar = SLOT_MIN_PERCENTILE[index] ?? SLOT_MIN_PERCENTILE[SLOT_MIN_PERCENTILE.length - 1]!;

    // Widening is a last resort for a Catalog too thin to fill an era — a Set of
    // five beats a Set of four, but the era guarantee is tried first, and the
    // difficulty floor is never given up even when the era is.
    const pick =
      pickFrom(headOf(era, bar), random, used) ??
      pickFrom(headOf(tracks, bar), random, used) ??
      pickFrom(tracks, random, used);
    if (!pick) return;

    used.add(pick.id);
    set.push(pick);
  });

  return set;
}

/**
 * How hard a Slot is meant to feel, for the UI to signal.
 *
 * Derived from the same SLOT_MIN_PERCENTILE the draw uses, so the dots on screen
 * cannot drift out of step with the actual difficulty.
 */
export function slotDifficulty(index: number): 1 | 2 | 3 | 4 | 5 {
  return (Math.min(index, SLOT_MIN_PERCENTILE.length - 1) + 1) as 1 | 2 | 3 | 4 | 5;
}
